/**
 * The composer's dictation surfaces: the recording dialog, the one-line status
 * under Notes while a recording is on its way, and the clarifying questions
 * the pipeline asks afterwards (`architecture/0025`).
 *
 * State lives in `useDictation`, owned by the composer, so closing the dialog
 * does not end a recording that is being sent — the words still arrive in
 * Notes, and the line under the field says what is happening meanwhile.
 */
import { useState } from "react";
import { Download, Loader2, Mic, Square } from "lucide-react";
import {
  MAX_DICTATION_SECONDS,
  capCountdown,
  recordingClock,
  type DictationClarification,
} from "@gb/contracts";
import { SectionLabel } from "@/components/gb/section-label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { DictationPhase, useDictation } from "./use-dictation";

export const SENDING_LINE = "Sending the recording…";
/** Recording has ended by the time this shows; it never says "listening". */
export const TRANSCRIBING_LINE = "GroundBrain is writing down what you said…";

type Dictation = ReturnType<typeof useDictation>;

export function DictationDialog({
  open,
  onOpenChange,
  dictation,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dictation: Dictation;
}) {
  const { phase } = dictation;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing mid-sentence keeps what was said: it stops and sends, and
        // the words arrive in Notes. Only *Discard* throws a take away.
        if (!next && phase.kind === "recording") dictation.stop();
        if (!next && phase.kind === "starting") dictation.discard();
        onOpenChange(next);
      }}
    >
      {/* Stacked above the composer — the kit's z-ladder is 60 < 65 < 70. */}
      <DialogContent size="sm" overlayClassName="z-65">
        <DialogHeader>
          <DialogTitle>Dictation</DialogTitle>
          <DialogCloseButton />
        </DialogHeader>
        <DialogBody className="space-y-3">
          <PhaseBody phase={phase} />
        </DialogBody>
        <DialogFooter>
          <PhaseActions phase={phase} dictation={dictation} onClose={() => onOpenChange(false)} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhaseBody({ phase }: { phase: DictationPhase }) {
  switch (phase.kind) {
    case "idle":
      return (
        <DialogDescription>
          Speak the note. GroundBrain writes down what you said and adds it to
          Notes, where you check it before saving. Up to{" "}
          {MAX_DICTATION_SECONDS / 60} minutes.
        </DialogDescription>
      );
    case "starting":
      return (
        <DialogDescription>
          Waiting for the microphone — your browser may ask for permission.
        </DialogDescription>
      );
    case "recording": {
      const countdown = capCountdown(phase.elapsedSeconds);
      return (
        <>
          <p className="flex items-center gap-2.5 text-[0.95rem] font-semibold text-ink" aria-live="polite">
            <span aria-hidden className="size-2.5 animate-pulse rounded-full bg-danger" />
            Recording · <span className="font-mono">{recordingClock(phase.elapsedSeconds)}</span>
          </p>
          {countdown && (
            <p className="text-[0.82rem] font-semibold text-warn-text">{countdown}</p>
          )}
        </>
      );
    }
    case "sending":
    case "transcribing":
      return (
        <>
          <p className="flex items-center gap-2.5 text-[0.9rem] text-ink" aria-live="polite">
            <Loader2 aria-hidden size={16} className="animate-spin" />
            {phase.kind === "sending" ? SENDING_LINE : TRANSCRIBING_LINE}
          </p>
          <DialogDescription>
            You can close this and keep typing. The words are added to Notes
            when they are ready.
          </DialogDescription>
        </>
      );
    case "failed":
      return (
        <p
          role="alert"
          className="rounded-input border-[1.5px] border-warn/40 bg-warn-bg px-3.5 py-2.5 text-[0.85rem] leading-[1.5] font-semibold text-warn-text"
        >
          {phase.message}
        </p>
      );
  }
}

function PhaseActions({
  phase,
  dictation,
  onClose,
}: {
  phase: DictationPhase;
  dictation: Dictation;
  onClose: () => void;
}) {
  switch (phase.kind) {
    case "idle":
      return (
        <Button size="footer" type="button" onClick={() => void dictation.start()}>
          <Mic size={16} />
          Start recording
        </Button>
      );
    case "starting":
      return (
        <Button size="footer" type="button" disabled>
          <Loader2 size={16} className="animate-spin" />
          Starting…
        </Button>
      );
    case "recording":
      return (
        <>
          <Button variant="secondary" size="footer" type="button" onClick={dictation.discard}>
            Discard
          </Button>
          <Button size="footer" type="button" onClick={dictation.stop}>
            <Square size={14} />
            Stop and transcribe
          </Button>
        </>
      );
    case "sending":
    case "transcribing":
      return (
        <Button variant="secondary" size="footer" type="button" onClick={onClose}>
          Keep typing
        </Button>
      );
    case "failed":
      return (
        <>
          {phase.hasRecording && (
            <Button variant="secondary" size="footer" type="button" onClick={dictation.download}>
              <Download size={16} />
              Download recording
            </Button>
          )}
          {phase.retryable ? (
            <Button size="footer" type="button" onClick={dictation.retry}>
              Try again
            </Button>
          ) : (
            <Button variant="secondary" size="footer" type="button" onClick={onClose}>
              Type instead
            </Button>
          )}
        </>
      );
  }
}

/**
 * The line under Notes while the dialog is closed and a recording is still
 * on its way — or failed on the way, so the failure is not lost behind a
 * closed dialog.
 */
export function DictationStatus({
  phase,
  onOpen,
}: {
  phase: DictationPhase;
  onOpen: () => void;
}) {
  if (phase.kind === "sending" || phase.kind === "transcribing") {
    return (
      <p className="mt-2 flex items-center gap-2 text-[0.82rem] text-muted" aria-live="polite">
        <Loader2 aria-hidden size={14} className="animate-spin" />
        {phase.kind === "sending" ? SENDING_LINE : TRANSCRIBING_LINE}
      </p>
    );
  }
  if (phase.kind === "failed") {
    return (
      <p className="mt-2 flex flex-wrap items-center gap-2 text-[0.82rem] font-semibold text-warn-text">
        {phase.message}
        <Button variant="ghost" size="sm" type="button" onClick={onOpen}>
          Open dictation
        </Button>
      </p>
    );
  }
  return null;
}

/** The head of the questions block — the phone's words. */
function clarificationsIntro(count: number): string {
  return count === 1
    ? "One thing GroundBrain wants to check:"
    : `${count} things GroundBrain wants to check:`;
}

/**
 * Under the questions once one is answered. The answer is recorded in its
 * own right — it is the corpus — but it does not rewrite a field, so the
 * sentence hands the pen back.
 */
export const ANSWER_RECORDED = "Recorded — make sure the fields say the same.";

export type ClarificationAnswerInput = { chosenIndex: number } | { typedText: string };

/**
 * The pipeline's materially uncertain spans, put to the person who said them
 * as decisions: one click on a candidate reading, or their own words, which
 * always win. An answered question leaves, so what is outstanding is always
 * what is on screen — the phone's rule (MOB-67).
 */
export function ClarificationCards({
  questions,
  onAnswer,
}: {
  questions: DictationClarification[];
  onAnswer: (clarificationId: string, answer: ClarificationAnswerInput) => void;
}) {
  const open = questions.filter((question) => !question.answer);
  const answered = questions.length - open.length;
  if (questions.length === 0) return null;
  return (
    <section className="space-y-2.5">
      {open.length > 0 && <SectionLabel>{clarificationsIntro(open.length)}</SectionLabel>}
      {open.map((question) => (
        <ClarificationCard key={question.id} question={question} onAnswer={onAnswer} />
      ))}
      {answered > 0 && <p className="text-[0.82rem] text-muted">{ANSWER_RECORDED}</p>}
    </section>
  );
}

function ClarificationCard({
  question,
  onAnswer,
}: {
  question: DictationClarification;
  onAnswer: (clarificationId: string, answer: ClarificationAnswerInput) => void;
}) {
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState("");
  const submit = () => {
    const value = typed.trim();
    if (value.length === 0) return;
    onAnswer(question.id, { typedText: value });
  };
  return (
    <div className="space-y-2 rounded-card border-[1.5px] border-brand-border bg-brand-tint-3 p-3">
      <p className="text-[0.9rem] font-semibold text-ink">{question.question}</p>
      {question.quote && (
        <q className="block text-[0.82rem] leading-[1.45] text-ink-prose">{question.quote}</q>
      )}
      <div className="flex flex-wrap gap-2">
        {question.options.map((option, index) => (
          <Button
            key={`${question.id}-${index}`}
            variant="outline"
            size="sm"
            type="button"
            onClick={() => onAnswer(question.id, { chosenIndex: index })}
          >
            {option}
          </Button>
        ))}
        <Button
          variant="secondary"
          size="sm"
          type="button"
          aria-expanded={typing}
          onClick={() => setTyping((now) => !now)}
        >
          Type it
        </Button>
      </div>
      {typing && (
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Input
            aria-label="What you actually said"
            placeholder="What you actually said"
            value={typed}
            maxLength={300}
            onChange={(event) => setTyped(event.target.value)}
          />
          <Button size="sm" type="submit" disabled={typed.trim().length === 0}>
            Save
          </Button>
        </form>
      )}
    </div>
  );
}
